<template>
  <div class="sidebar-container">
    <el-scrollbar wrap-class="scrollbar-wrapper">
      <el-menu
        class="sidebar-el-menu"
        :default-active="onRoutes"
        :collapse="collapse"
        :background-color="variables.menuBg"
        :text-color="variables.menuText"
        :active-text-color="variables.menuActiveText"
        unique-opened
        router
      >
        <template v-for="(item, index) in MENU.MENUS">
          <!-- ===== 有 children 且满足条件：渲染为可展开的 el-sub-menu =====
               条件：children>=2 或 有alwaysShow强制展开 或 唯一子项还有更深children
               否则（只有1个浅层子项且无强制展开）走 else 分支平铺优化，保留旧版交互。
          -->
          <template v-if="MENU.shouldShowAsSubMenu(item)">
            <el-sub-menu :index="MENU.resolveMenuIndex(item)" :key="'sub-' + index + '-' + item.path">
              <template #title>
                <item
                  v-if="item.meta"
                  :icon="MENU.getItemIcon(item)"
                  :title="MENU.getItemTitle(item)"
                />
                <span v-else>{{ item.name || item.path }}</span>
              </template>
              <!-- ===== children 再遍历一层 ===== -->
              <template v-for="(child, childIdx) in MENU.visibleChildren(item)">
                <!-- child 也有自己的 children（三级）：继续嵌套 sub-menu -->
                <template v-if="MENU.hasVisibleChildren(child)">
                  <el-sub-menu
                    :index="MENU.resolveMenuIndex(child, item)"
                    :key="'sub2-' + index + '-' + childIdx + '-' + child.path"
                  >
                    <template #title>
                      <item
                        v-if="child.meta"
                        :icon="MENU.getItemIcon(child)"
                        :title="MENU.getItemTitle(child)"
                      />
                      <span v-else>{{ child.name || child.path }}</span>
                    </template>
                    <el-menu-item
                      v-for="(grandChild, gIdx) in MENU.visibleChildren(child)"
                      :key="'leaf3-' + index + '-' + childIdx + '-' + gIdx + '-' + grandChild.path"
                      :index="MENU.resolveMenuIndex(grandChild, child, item)"
                    >
                      <item
                        v-if="grandChild.meta"
                        :icon="MENU.getItemIcon(grandChild)"
                        :title="MENU.getItemTitle(grandChild)"
                      />
                      <span v-else>{{ grandChild.name || grandChild.path }}</span>
                    </el-menu-item>
                  </el-sub-menu>
                </template>
                <!-- child 是叶子节点：直接 el-menu-item -->
                <el-menu-item
                  v-else
                  :key="'leaf-' + index + '-' + childIdx + '-' + child.path"
                  :index="MENU.resolveMenuIndex(child, item)"
                >
                  <item
                    v-if="child.meta"
                    :icon="MENU.getItemIcon(child)"
                    :title="MENU.getItemTitle(child)"
                  />
                  <span v-else>{{ child.name || child.path }}</span>
                </el-menu-item>
              </template>
            </el-sub-menu>
          </template>

          <!-- ===== 无 children / 只有 1 个 visible child 时也想平铺？no！现在只有 visible children 为 0 才走平铺：
                     但要兼容 old behavior：有些路由（如 /client/list）外层包一层 Home，其实只有 1 个真正的子路由，
                     这种情况下我们不想渲染一个父级再包一个子级。怎么办？
                     加个判断：如果只有 1 个 visible child 且 item.noAutoExpand !== true 且 !item.alwaysShow && !(item.meta&&item.meta.alwaysShow)，
                     则渲染那个唯一子路由作为平铺的 el-menu-item！ -->
          <template v-else>
            <!-- old: 单 child 优化平铺模式 -->
            <el-menu-item
              v-if="MENU.getSingleLeaf(item)"
              :key="'single-' + index + '-' + item.path"
              :index="MENU.resolveMenuIndex(MENU.getSingleLeaf(item), item)"
            >
              <item
                :icon="MENU.getSingleLeaf(item).meta && (MENU.getSingleLeaf(item).meta.icon || (item.meta && item.meta.icon))"
                :title="MENU.getItemTitle(MENU.getSingleLeaf(item), item)"
              />
            </el-menu-item>
            <!-- 真·没有任何 children 的独立路由（极少） -->
            <el-menu-item
              v-else
              :key="'alone-' + index + '-' + item.path"
              :index="MENU.resolveMenuIndex(item)"
            >
              <item
                v-if="item.meta"
                :icon="MENU.getItemIcon(item)"
                :title="MENU.getItemTitle(item)"
              />
              <span v-else>{{ item.name || item.path }}</span>
            </el-menu-item>
          </template>
        </template>
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script>
import path from 'path'
import { mapGetters } from 'vuex'
import Item from './item'
import variables from './index.scss'

export default {
  components: { Item },
  computed: {
    ...mapGetters([
      'collapse',
      'routes'
    ]),
    onRoutes() {
      return this.$route.path
    },
    collapse() {
      return this.collapse
    },
    variables() {
      return variables
    },
    MENU: function () {
      const _this = this
      const isExternal = (p) => /^(https?:|mailto:|tel:)/.test(p)
      const resolvePath = (routePath, basePath) => {
        if (isExternal(routePath)) return routePath
        if (basePath && isExternal(basePath)) return basePath
        // 1) 如果已经是绝对路径（以 / 开头），直接返回
        if (routePath && routePath.indexOf('/') === 0) return routePath
        // 2) 否则按 basePath 拼接
        const bp = basePath || '/'
        return path.resolve(bp, routePath)
      }

      return {
        MENUS: (function () {
          return _this.routes.filter(item => {
            // 基础过滤：hidden 路由不显示
            if (item.hidden) return false
            // 去掉没有任何可见 children、且本身没有有效 title 的占位路由
            // （比如 constantRoutes 里的 {path:'/', component: HomeBase, children全hidden} 这种
            //   或其他 path='/' 且 redirect 无 dashboard 子项的壳子路由）
            const vc = (!item.children || !Array.isArray(item.children))
              ? []
              : item.children.filter(c => !c.hidden)
            const hasValidTitle = item.name
              || (item.meta && item.meta.title && item.meta.title !== '--')
            // 如果没有可见 children，且本身没有有效 title → 视为占位壳子，不显示
            if (vc.length === 0 && !hasValidTitle) return false
            // 如果 path 是 '/' 且没有可见 children 也没有 redirect/nav → 也不显示（避免空 "/" 菜单项）
            if (item.path === '/' && vc.length === 0 && !(item.meta && item.meta.nav)) return false
            return true
          })
        })(),
        // 过滤掉 hidden=true 的 children
        visibleChildren(route) {
          if (!route || !route.children || !Array.isArray(route.children)) return []
          return route.children.filter(c => !c.hidden)
        },
        // 是否有可见的 children
        hasVisibleChildren(route) {
          return this.visibleChildren(route).length > 0
        },
        // ★ 关键：这个路由是否应该显示为可展开的 el-sub-menu
        // 返回 true → el-sub-menu（有父级标题 + 展开箭头）
        // 返回 false → else 分支（getSingleLeaf 做单 child 平铺优化）
        shouldShowAsSubMenu(item) {
          if (!item) return false
          const vcs = this.visibleChildren(item)
          // 1) 根本没 children，不可能展开
          if (vcs.length === 0) return false
          // 2) 有 2 个及以上 children，一定展开（比如 /settlement）
          if (vcs.length >= 2) return true
          // 3) 只有 1 个 children，但用户配了 alwaysShow（顶级或 meta 里任意一个）
          if (item.alwaysShow || (item.meta && item.meta.alwaysShow)) return true
          // 4) 只有 1 个 children，但这个 children 本身还有更深 children（三级菜单）
          if (this.hasVisibleChildren(vcs[0])) return true
          // 其他情况：1 个浅层 child + 无 alwaysShow → 平铺（不走 sub-menu）
          return false
        },
        // 路由标题
        getItemTitle(item, fallbackItem) {
          if (item && item.meta && item.meta.title) return item.meta.title
          if (fallbackItem && fallbackItem.meta && fallbackItem.meta.title) return fallbackItem.meta.title
          if (item && item.name) return item.name
          if (item && item.path) return item.path
          return '--'
        },
        // 路由图标
        getItemIcon(item) {
          if (!item) return ''
          if (item.meta && item.meta.icon) return item.meta.icon
          return ''
        },
        // 解析菜单 index（路由跳转全路径）
        resolveMenuIndex(item, ...parents) {
          if (!item) return ''
          // 如果 item.path 本身就是绝对路径，直接用
          if (item.path && item.path.indexOf('/') === 0 && !isExternal(item.path)) {
            return item.path
          }
          // 否则按 parents 一层层往外找 basePath
          let base = ''
          for (let i = parents.length - 1; i >= 0; i--) {
            const p = parents[i]
            if (p && p.path) {
              base = resolvePath(p.path, base || undefined)
              if (base && base.indexOf('/') === 0) break
            }
          }
          const ret = resolvePath(item.path, base || undefined)
          return ret
        },
        // 只有 1 个 visible child，且用户没强制要求展开时，平铺那个子节点
        // ★ 关键：如果 item.alwaysShow 或 item.meta.alwaysShow 为 true，强制返回 null → 让外层渲染 el-sub-menu
        getSingleLeaf(item) {
          if (!item) return null
          const forceSubMenu = !!(item.alwaysShow || (item.meta && item.meta.alwaysShow))
          if (forceSubMenu) return null // 用户明确要展开模式，不走单 child 平铺优化
          const vcs = this.visibleChildren(item)
          // 可见子路由数量 = 1 且 这个子路由没有更深的可见 children
          if (vcs.length === 1) {
            const onlyOne = vcs[0]
            if (!this.hasVisibleChildren(onlyOne)) return onlyOne
            return null
          }
          // 0 个可见 children：返回 null，让外层走 item 本身或空
          return null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "index";
</style>
